import React, { useMemo, useState, useCallback } from 'react';
import { Box, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PaymentIcon from '@mui/icons-material/Payment';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { WorkflowPage, PaymentModal } from '../../../components';
import { useEntityAlertMap } from '../../../hooks/useEntityAlertMap';
import ExpenseDetailModal from './ExpenseDetailModal';
import { FORM_CONFIGS } from '../../../constants/formConfigurations';
import { TABLE_COLUMNS, FILTER_CONFIGS, SEARCH_CONFIGS } from '../../../constants/tableConfigurations';
import { profileLeavesTabStyles } from '../../ProfilePage/tabs/ProfileLeavesTab.styles';
import { useAuth } from '../../../hooks';
import { useExpenses } from '../../../hooks/useExpenses';
import { useExpenseTypes } from '../../../hooks/useExpenseTypes';
import { useUsers } from '../../../hooks/useUsers';
import { useNotification } from '../../../hooks/useNotification';
import { hasuraClient } from '../../../utils/graphqlClient';
import { APPROVE_EXPENSE_MUTATION, REJECT_EXPENSE_MUTATION, UPDATE_EXPENSE_PAYMENT_MUTATION } from '../../../graphql/mutations/expenses';

const ExpensesPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getRowSx } = useEntityAlertMap('expense');
  const {
    expenses,
    loading,
    fetchData,
    handleFormSubmit,
    serverPagination,
    page,
    pageSize,
    totalCount,
    hasNextPage,
    setPage,
    setPageSize,
    setExtraWhereCondition,
  } = useExpenses(true, null, { paginated: true });
  const { expenseTypes, fetchData: fetchExpenseTypes } = useExpenseTypes();
  const { users } = useUsers();
  const { showNotification } = useNotification();
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailExpense, setDetailExpense] = useState(null);

  const filterConfigs = useMemo(() => {
    return FILTER_CONFIGS.expenses.filter(config => {
      if (config.showIfRoot && user?.role !== 'ROOT') return false;
      return true;
    });
  }, [user?.role]);

  const formFields = useMemo(() => {
    let fields = [...FORM_CONFIGS.expenses.fields];
    
    // Tüm kullanıcılar için kullanıcı seçimi ekle
    let userOptions = [];
    
    if (user?.role === 'ROOT') {
      // ROOT kullanıcıları tüm kullanıcıları görebilir
      userOptions = users.map(u => ({ 
        value: u.id, 
        label: `${u.firstName} ${u.lastName} (${u.companyName || 'Şirket Yok'})`,
        companyId: u.companyId
      }));
    } else {
      userOptions = users
        .filter(u => u.companyId === user?.companyId)
        .map(u => ({ 
          value: u.id, 
          label: `${u.firstName} ${u.lastName}`,
          companyId: u.companyId
        }));
    }
    
    fields = [
      {
        name: 'userId',
        type: 'select',
        label: 'expenses.form.user',
        required: true,
        fullWidth: true,
        options: userOptions,
        validation: { required: true, requiredMessage: t('expenses.form.validation.userRequired') },
        onChange: (value, formData, setFormData) => {
          const selectedUser = users.find(u => u.id === value);
          if (selectedUser) {
            setFormData(prev => ({
              ...prev,
              selectedUserCompanyId: selectedUser.companyId
            }));
          }
        }
      },
      ...fields,
    ];
    
    fields = fields.map(field => {
      if (field.name === 'expenseType') {
        return {
          ...field,
          options: expenseTypes
            .filter(et => et.expense_type_status)
            .map(et => ({
              value: et.id,
              label: et.expense_type_name,
            })),
        };
      }
      return field;
    });
    
    return fields;
  }, [expenseTypes, users, user?.role, user?.companyId]);

  const statusCounts = useMemo(() => {
    if (!Array.isArray(expenses)) return { all: 0, PENDING: 0, APPROVED: 0, REJECTED: 0 };
    const counts = { all: expenses.length, PENDING: 0, APPROVED: 0, REJECTED: 0 };
    expenses.forEach(expense => {
      if (counts.hasOwnProperty(expense.status)) counts[expense.status]++;
    });
    return counts;
  }, [expenses]);

  const handleApprove = async (expenseId) => {
    try {
      await hasuraClient.mutate(APPROVE_EXPENSE_MUTATION, {
        id: expenseId,
        updateFields: {
          status: 'APPROVED',
          approving_manager_id: user?.id,
          approving_date: new Date().toISOString(),
        },
      });
      await fetchData();
      showNotification(t('expenses.actions.approved'), 'success');
      return { success: true };
    } catch (error) {
      console.error('Error approving expense:', error);
      showNotification(t('expenses.actions.approveError'), 'error');
      return { success: false, error: error.message };
    }
  };

  const handleReject = async (expenseId, reason) => {
    try {
      await hasuraClient.mutate(REJECT_EXPENSE_MUTATION, {
        id: expenseId,
        updateFields: {
          status: 'REJECTED',
          reject_reason: reason,
          approving_manager_id: user?.id,
          approving_date: new Date().toISOString(),
        },
      });
      await fetchData();
      showNotification(t('expenses.actions.rejected'), 'success');
      return { success: true };
    } catch (error) {
      console.error('Error rejecting expense:', error);
      showNotification(t('expenses.actions.rejectError'), 'error');
      return { success: false, error: error.message };
    }
  };

  const columns = useMemo(() =>
    TABLE_COLUMNS.expenses.map(col =>
      col.field === 'status'
        ? {
            ...col,
            render: (value, row) => {
              const getStatusChip = (status, color, label) => (
                <Box component="span" sx={{ display: 'inline-block', pointerEvents: 'none' }}>
                  <Chip
                    label={label}
                    color={color}
                    size="small"
                    sx={profileLeavesTabStyles.statusChip(status)}
                  />
                </Box>
              );

              switch (value) {
                case 'REJECTED':
                  return getStatusChip('REJECTED', 'error', t('expenses.status.rejected'));
                case 'APPROVED':
                  return getStatusChip('APPROVED', 'success', t('expenses.status.approved'));
                case 'PENDING':
                default:
                  return getStatusChip('PENDING', 'warning', t('expenses.status.pending'));
              }
            },
          }
        : col
    ),
  [t]);

  const handlePayment = useCallback((expenseId) => {
    const expense = expenses.find(e => e.id === expenseId);
    if (expense) {
      setSelectedExpense(expense);
      setPaymentModalOpen(true);
    }
  }, [expenses]);

  const handlePaymentConfirm = useCallback(async (paidAmount) => {
    if (!selectedExpense) return;
    
    setIsProcessingPayment(true);
    try {
      await hasuraClient.mutate(UPDATE_EXPENSE_PAYMENT_MUTATION, {
        id: selectedExpense.id,
        paidAmount: paidAmount,
      });
      await fetchData();
      showNotification(t('expenses.actions.paymentSuccess'), 'success');
      setPaymentModalOpen(false);
      setSelectedExpense(null);
    } catch (error) {
      console.error('Error updating payment:', error);
      showNotification(t('expenses.actions.paymentError'), 'error');
    } finally {
      setIsProcessingPayment(false);
    }
  }, [selectedExpense, fetchData, showNotification, t]);

  const handlePaymentClose = useCallback(() => {
    if (!isProcessingPayment) {
      setPaymentModalOpen(false);
      setSelectedExpense(null);
    }
  }, [isProcessingPayment]);

  const handleRowAction = useCallback((actionType, row) => {
    if (actionType === 'payment') {
      handlePayment(row.id);
      return true;
    }
    if (actionType === 'review') {
      setDetailExpense(row);
      setDetailModalOpen(true);
      return true;
    }
    return false;
  }, [handlePayment]);

  const rowActions = useCallback((row) => {
    const actions = [];

    if (row.status === 'APPROVED') {
      actions.push({
        type: 'review',
        label: 'expenses.actions.review',
        color: 'info',
        icon: <SearchIcon fontSize="small" />,
      });
      actions.push({
        type: 'payment',
        label: 'expenses.actions.payment',
        color: 'primary',
        icon: <PaymentIcon fontSize="small" />,
      });
    } else if (row.status === 'REJECTED') {
      actions.push({
        type: 'review',
        label: 'expenses.actions.review',
        color: 'info',
        icon: <SearchIcon fontSize="small" />,
      });
    } else if (row.status === 'PENDING') {
      actions.push({
        type: 'review',
        label: 'expenses.actions.review',
        color: 'info',
        icon: <SearchIcon fontSize="small" />,
      });
      actions.push({
        type: 'approve',
        label: 'expenses.actions.approve',
        color: 'success',
        icon: <CheckCircleIcon fontSize="small" />,
      });
      actions.push({
        type: 'reject',
        label: 'expenses.actions.reject',
        color: 'error',
        icon: <CancelIcon fontSize="small" />,
      });
    }

    return actions;
  }, []);

  const pageConfig = useMemo(() => ({
    columns,
    filterConfigs,
    searchConfig: SEARCH_CONFIGS.expenses,
    formConfig: { 
      fields: formFields,
      title: FORM_CONFIGS.expenses.title
    },
  }), [columns, filterConfigs, formFields]);

  return (
    <>
      <WorkflowPage
        entityType="expenses"
        data={expenses}
        loading={loading}
        columns={pageConfig.columns}
        formFields={pageConfig.formConfig.fields}
        filterConfigs={pageConfig.filterConfigs}
        searchConfig={pageConfig.searchConfig}
        formConfig={pageConfig.formConfig}
        statusCounts={statusCounts}
        onFormSubmit={handleFormSubmit}
        onApprove={handleApprove}
        onReject={handleReject}
        rowActions={rowActions}
        onRowAction={handleRowAction}
        canAdd={true}
        addButtonText={t('expenses.actions.add')}
        addButtonIcon={<AddIcon />}
        currentPage={t('pages.expenses')}
        showViewGuard={false}
        exportButton={true}
        getRowSx={getRowSx}
        serverPagination={serverPagination}
        serverPage={page}
        serverPageSize={pageSize}
        serverTotalCount={totalCount}
        serverHasNextPage={hasNextPage}
        onServerPageChange={setPage}
        onServerPageSizeChange={setPageSize}
        setExtraWhereCondition={setExtraWhereCondition}
      />
      <ExpenseDetailModal
        open={detailModalOpen}
        onClose={() => { setDetailModalOpen(false); setDetailExpense(null); }}
        expense={detailExpense}
      />
      <PaymentModal
        open={paymentModalOpen}
        onClose={handlePaymentClose}
        onConfirm={handlePaymentConfirm}
        expenseRequest={selectedExpense}
        loading={isProcessingPayment}
      />
    </>
  );
};

export default ExpensesPage;